import { formatDate } from 'date-fns/format';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import { Invoice, InvoiceTransactionStatus } from '@/models/Invoice';
import { formatPrice } from '@/utils/currency';
import { Link } from 'react-router-dom';

interface InvoiceTableProps {
  invoices: Invoice[];
  page: number;
  itemsPerPage: number;
  totalCount?: number;
  estimatedTotalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  onChangePage: (page: number) => void;
  showPaymentStatus?: boolean;
  getInvoicePath?: (invoiceId: string) => string;
}

const invoiceStatusLabel: Record<Invoice['status'], string> = {
  pending: 'Pending',
  succeeded: 'Paid',
  canceled: 'Canceled',
};

const paymentStatusLabel: Record<InvoiceTransactionStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  succeeded: 'Succeeded',
  failed: 'Failed',
  canceled: 'Canceled',
};

const InvoiceTable = ({
  invoices,
  page,
  itemsPerPage,
  totalCount,
  estimatedTotalCount,
  isLoading,
  isFetching,
  onChangePage,
  showPaymentStatus = false,
  getInvoicePath,
}: InvoiceTableProps) => {
  return (
    <div className="table-container" data-testid="invoice-list">
      <div className="table-wrapper">
        <table className={isLoading ? 'loading' : ''} data-testid="invoice-list-table">
          <thead>
            <tr>
              <th className="min-w-40">Invoice</th>
              <th className="min-w-40">Created At</th>
              <th className="min-w-56">Billing Period</th>
              <th className="min-w-40">Total</th>
              <th className="min-w-40">Invoice Status</th>
              {showPaymentStatus ? <th className="min-w-40">Payment Status</th> : null}
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice.id} data-testid="invoice-row" data-invoice-id={invoice.id}>
                <td>
                  <div className="whitespace-nowrap font-medium text-slate-900">
                    {getInvoicePath ? (
                      <Link className="hover:underline" to={getInvoicePath(invoice.id)}>
                        {invoice.invoiceNumber}
                      </Link>
                    ) : (
                      invoice.invoiceNumber
                    )}
                  </div>
                  <div className="text-sm text-slate-500" data-testid="invoice-description">
                    {invoice.description}
                  </div>
                </td>
                <td>{formatDate(invoice.createdAt, 'd MMM yyyy')}</td>
                <td>
                  {formatDate(invoice.period.start, 'd MMM yyyy')} -{' '}
                  {formatDate(invoice.period.end, 'd MMM yyyy')}
                </td>
                <td>{formatPrice(invoice.totalAmount)}</td>
                <td data-testid="invoice-status">{invoiceStatusLabel[invoice.status]}</td>
                {showPaymentStatus ? (
                  <td data-testid="invoice-payment-status">
                    {invoice.paymentStatus
                      ? paymentStatusLabel[invoice.paymentStatus]
                      : 'Not started'}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
        {isLoading ? (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          ''
        )}
      </div>

      {!isLoading && invoices.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-600">
          No invoices found.
        </div>
      )}

      {isFetching && !isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <SpinnerIcon className="h-4 w-4" />
          Refreshing invoices...
        </div>
      ) : null}

      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          totalCount={totalCount || 0}
          estimatedTotalCount={estimatedTotalCount}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default InvoiceTable;
